/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: wangchao
 *  创建时间: 2026-08-28 09:05
 *  文件名: src/components/ppt-learning-plan-viewer/index.tsx
 *  功能描述: PPT学案联动预览组件，左侧 PPT 幻灯片预览 + 右侧 Word 文档预览，并提供命令式弹窗调用
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { WordViewer } from './word-viewer';
import styles from './index.module.less';
import { PPTViewer } from './ppt-viewer';
import { LearningPlanPreviewContext } from './types';
import bulb_icon from './bulb.svg';
import { FileDownloadButton } from './download-button';

interface PPTLearningPlanViewerProps {
  pptFileId: string;
  wordFileId: string;
  previewContext: LearningPlanPreviewContext;
}

/**
 * PPT 与 Word 左右分栏联动预览容器
 */
function PPTLearningViewer(props: PPTLearningPlanViewerProps) {
  const { pptFileId, wordFileId, previewContext } = props;
  return (
    <div className={styles.split_viewer}>
      <div className={styles.ppt_section}>
        <PPTViewer fileId={pptFileId} previewContext={previewContext} />
      </div>
      <div className={styles.word_section}>
        <WordViewer fileId={wordFileId} previewContext={previewContext} />
      </div>
    </div>
  );
}

/**
 * 命令式打开 PPT-学案联动预览弹窗：渲染到 body 下独立容器，返回销毁函数供调用方主动关闭
 *
 * @returns 销毁函数（立即卸载无关闭动画，重复调用安全）
 */
function showPPTLearningPlanViewerModal(
  props: PPTLearningPlanViewerProps & { modalProps?: ModalProps },
): () => void {
  const { modalProps, ...rest } = props;
  const userOnCancel = modalProps && modalProps.onCancel;
  const userAfterClose = modalProps && modalProps.afterClose;
  const container = document.createElement('div');
  document.body.appendChild(container);

  /**
   * 卸载弹窗组件并从 body 移除挂载容器，避免 DOM 泄漏
   */
  function destroy() {
    if (userAfterClose) {
      userAfterClose();
    }
    ReactDOM.unmountComponentAtNode(container);
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  ReactDOM.render(
    <Modal
      visible
      footer={null}
      title={
        <div>
          <span style={{ fontSize: 14, color: '#333', fontWeight: 'bold' }}>课中学案</span>
          <span style={{ fontSize: 12, marginLeft: 12, color: 'rgb(19, 41, 64,0.72)' }}>
            <img src={bulb_icon} style={{ height: '1em', verticalAlign: '-0.1em' }} />
            <span style={{ marginLeft: 6 }}>
              本学案由 AI 基于提前 PPT
              生成，收录已归纳的词汇/短语/句式/语法等内容。请老师核查学案内容，也可下载后按需增删内容，打印给学生课中使用。
            </span>
          </span>
          <FileDownloadButton
            fileId={rest.wordFileId}
            previewContext={rest.previewContext}
            className={styles.download_btn}
          >
            下载学案
          </FileDownloadButton>
        </div>
      }
      maskClosable={false}
      className={styles.preview_modal}
      centered
      width="min(90vw, 1560px)"
      bodyStyle={{ height: '85vh', overflow: 'auto', padding: '0' }}
      {...modalProps}
      onCancel={(event: React.MouseEvent<HTMLElement>) => {
        if (userOnCancel) {
          userOnCancel(event);
        }
        destroy();
      }}
    >
      <PPTLearningViewer {...rest} />
    </Modal>,
    container,
  );

  return destroy;
}

export { PPTLearningViewer, showPPTLearningPlanViewerModal };
export type { LearningPlanPreviewContext } from './types';
